import React, { useContext } from 'react';
import { useIntl } from 'react-intl';
import Tooltip from 'components/core/Tooltip';
import useCopyToClipboard from 'hooks/useClipboard';
import _get from 'lodash/get';
import { ReactComponent as CopyIcon } from 'assets/icons/copy.svg';
import { ReactComponent as CheckIcon } from 'assets/icons/check.svg';
import { ReactComponent as BinIcon } from 'assets/icons/bin.svg';
import { ReactComponent as ArchiveIcon } from 'assets/icons/archive.svg';
import styles from './style.cssmodule.css';
import { formatAddress } from '../../utils/utils';
import { AccountsContext } from '../../contexts/AccountsContext';
import Status from '../Status/Status';

function AccountCard(account) {
  const { formatMessage: t } = useIntl();
  const {
    copyToClipboard, copied, justCopied, resetJustCopied,
  } = useCopyToClipboard();
  const { handleOpenModalArchive, handleOpenModalDeleteAccount } = useContext(AccountsContext);

  const id = _get(account, 'id', null);

  // 🟠 Status
  const status = _get(account, 'status', null);
  const isArchived = status === 'ARCHIVED';

  // 👥 Username
  const firstname = _get(account, 'firstname', '');
  const lastname = _get(account, 'lastname', '');

  // 📨 Email
  const email = isArchived ? null : _get(account, 'email', '-');
  const emailJustCopied = justCopied && copied && copied === email;

  // 🤳 Phone
  const phone = isArchived ? null : _get(account, 'phone', '-');
  const phoneJustCopied = justCopied && copied && copied === phone;

  // 📮 Address
  const addressRaw = _get(account, 'invoiceAddress', null);
  const address = isArchived ? null : formatAddress(addressRaw);
  const addressJustCopied = justCopied && copied && copied === address;

  // 🚗 Carparks
  const carparksLinked = _get(account, 'carparks', []).map((carpark) => _get(carpark, 'name', '').replace(/^Parking\s/, ''));

  // 🔀 Accesses
  const totalAccesses = _get(account, 'totalAccesses', 0);
  const activeAccesses = _get(account, 'activeAccesses', 0);

  // 👥 Username
  let Firstname = null;
  let Lastname = null;

  if (firstname) Firstname = <span className={styles.firstname}>{firstname}</span>;
  if (lastname) Lastname = <span className={styles.lastname}>{lastname}</span>;

  // 💥 Actions
  let actions = [];
  const isArchivable = _get(account, 'isArchivable', false);
  const isDeletable = _get(account, 'isDeletable', false);

  let tooltipMessage = '';

  if (isArchivable) tooltipMessage = 'account.table.actions.archive';

  if (!isArchivable) {
    if (isArchived) {
      tooltipMessage = 'account.table.actions.cannotArchiveAlreadyArchived';
    } else {
      tooltipMessage = 'account.table.actions.cannotArchive';
    }
  }
  actions = [{
    label: 'archive',
    iconButton: (
      <Tooltip content={t({ id: tooltipMessage })}>
        <button style={{ opacity: isArchivable ? 1 : 0.5 }} disabled={!isArchivable} type="button" onClick={() => handleOpenModalArchive(id)}>
          <ArchiveIcon />
        </button>
      </Tooltip>
    ),
  }];

  if (isDeletable) {
    actions = [{
      label: 'delete',
      iconButton: (
        <Tooltip content={t({ id: 'account.table.actions.delete' })}>
          <button type="button" onClick={() => handleOpenModalDeleteAccount(id)}>
            <BinIcon />
          </button>
        </Tooltip>
      ),
    }];
  }

  const Actions = actions.map(({ label, iconButton }) => (
    <div key={label}>
      {iconButton}
    </div>
  ));

  return (
    <div key={account.id} className={styles.card}>
      {/* First row */}
      <div className={styles.firstRow}>
        <span className={styles.usernameOrLicensePlate}>
          {Firstname}
          {Firstname ? ' ' : null}
          {Lastname}
        </span>
        <div className={styles.statusAndActions}>
          <Status status={status} />
          <div className={styles.actions}>
            {Actions}
          </div>
        </div>
      </div>

      {/* 📨 Email */ }
      {email && (
        <div className={styles.row}>
          <span className={styles.email}>{email}</span>
          <Tooltip content={t({ id: emailJustCopied ? 'tooltip.copied' : 'tooltip.copy' })}>
            <div
              className={styles.copyIcon}
              onMouseLeave={() => resetJustCopied({ delay: 100 })}
              onClick={() => copyToClipboard(email)}
            >
              {emailJustCopied ? <CheckIcon width="16" height="16" /> : <CopyIcon width="16" height="16" />}
            </div>
          </Tooltip>
        </div>
      )}

      {/* 📲 Phone */ }
      {phone && (
        <div className={styles.row}>
          <span>{phone}</span>
          <Tooltip content={t({ id: phoneJustCopied ? 'tooltip.copied' : 'tooltip.copy' })}>
            <div
              className={styles.copyIcon}
              onMouseLeave={() => resetJustCopied({ delay: 100 })}
              onClick={() => copyToClipboard(phone)}
            >
              {phoneJustCopied ? <CheckIcon width="16" height="16" /> : <CopyIcon width="16" height="16" />}
            </div>
          </Tooltip>
        </div>
      )}

      {/* 📬 Address */ }
      {addressRaw && (
      <div className={styles.row}>
        <span>{address}</span>
        <Tooltip content={t({ id: addressJustCopied ? 'tooltip.copied' : 'tooltip.copy' })}>
          <div
            className={styles.copyIcon}
            onMouseLeave={() => resetJustCopied({ delay: 100 })}
            onClick={() => copyToClipboard(address)}
          >
            {addressJustCopied ? <CheckIcon width="16" height="16" /> : <CopyIcon width="16" height="16" />}
          </div>
        </Tooltip>
      </div>
      )}

      {/* 🚗 Carparks linked */}
      {carparksLinked.length > 0 && (
      <div className={styles.row}>
        {carparksLinked.length > 1 ? `${carparksLinked[0]} (+${carparksLinked.length - 1})` : carparksLinked[0]}
      </div>
      )}

      {/* 🔀 Total accesses */}
      <div className={styles.row}>
        Total number of accesses :
        {' '}
        {totalAccesses}
      </div>

      {/* 🔀✅ Active accesses */}
      <div className={styles.row}>
        Number of active accesses :
        {' '}
        {activeAccesses}
      </div>
    </div>
  );
}

export default AccountCard;



// WEBPACK FOOTER //
// ./routes/cpm/routes/accounts/components/AccountCard/AccountCard.jsx